<script setup>
import FoodFlowModal from '@/components/FoodFlow/FoodFlowModal.vue';
import { ref, computed } from 'vue';
import { useUserStore } from '@/stores/user';
import { storeToRefs } from 'pinia';
import { trans } from 'laravel-vue-i18n';

const props = defineProps({
    user: {
        type: Object,
        default: () => ({
            role: null,
            first_name: '',
            last_name: '',
            email: '',
            phone_number: '',
        })
    },
    mode: {
        type: String,
        default: 'Create'
    }
});

const emit = defineEmits(['close-modal']);
const form = ref({
    ...props.user,
    send_password: true,
    set_password: false,
    password: '',
    password_confirmation: '',
});

import { useOptionsStore } from '@/stores/options';
const optionsStore = useOptionsStore();
const { roleOptions } = storeToRefs(optionsStore);

const userStore = useUserStore();
const { storeUser, updateUser } = userStore;

if (props.user.hash && props.user.roles) {
    const matchedRole = roleOptions.value.find(role => role.id === props.user.roles[0].id);
    if (matchedRole) {
        form.value.role = matchedRole.name;
    }
}

const modalTitle = computed(() => props.mode === 'Edit' ? trans('label.edit') : trans('label.add'));
const saveButtonLabel = computed(() => props.mode === 'Edit' ? trans('label.save_changes') : trans('label.add_user'));

const passwordOption = computed({
    get: () => form.value.set_password ? 'manual' : 'email',
    set: (option) => {
        form.value.send_password = option === 'email';
        form.value.set_password = option === 'manual';
    }
});

const showPassword = ref(false);
const showPasswordConfirmation = ref(false);

const save = async () => {
    try {
        const action = props.mode === 'Create' ? storeUser : updateUser;

        await action({
            ...form.value,
            hash: props.user.hash,
        });

        emit('close-modal');
    } catch (error) {
        // Validation and server errors are surfaced centrally via the error store and toast interceptors.
    }
};
</script>
<template>
<FoodFlowModal>
    <template #header>{{ modalTitle }} {{ $t('label.user') }}</template>

    <form class="flex flex-col gap-4" @submit.prevent="save">
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
                <label class="mb-[7px] block text-[11px] font-bold uppercase tracking-[0.08em] text-[#375475]">{{ $t('label.first_name') }}</label>
                <input
                    v-model="form.first_name"
                    type="text"
                    :placeholder="$t('label.first_name')"
                    class="w-full rounded-[10px] border border-[#EDE8DF] bg-white px-3.5 py-2.5 text-[14px] text-[#1e2d3d] outline-none transition-colors focus:border-[#375475] focus:shadow-[0_0_0_3px_rgba(55,84,117,0.1)]">
            </div>
            <div>
                <label class="mb-[7px] block text-[11px] font-bold uppercase tracking-[0.08em] text-[#375475]">{{ $t('label.last_name') }}</label>
                <input
                    v-model="form.last_name"
                    type="text"
                    :placeholder="$t('label.last_name')"
                    class="w-full rounded-[10px] border border-[#EDE8DF] bg-white px-3.5 py-2.5 text-[14px] text-[#1e2d3d] outline-none transition-colors focus:border-[#375475] focus:shadow-[0_0_0_3px_rgba(55,84,117,0.1)]">
            </div>
        </div>
        <div>
            <label class="mb-[7px] block text-[11px] font-bold uppercase tracking-[0.08em] text-[#375475]">{{ $t('label.email') }}</label>
            <input
                v-model="form.email"
                type="email"
                :placeholder="$t('label.email')"
                class="w-full rounded-[10px] border border-[#EDE8DF] bg-white px-3.5 py-2.5 text-[14px] text-[#1e2d3d] outline-none transition-colors focus:border-[#375475] focus:shadow-[0_0_0_3px_rgba(55,84,117,0.1)]">
        </div>
        <div>
            <label class="mb-[7px] block text-[11px] font-bold uppercase tracking-[0.08em] text-[#375475]">{{ $t('label.role') }}</label>
            <select v-model="form.role" class="w-full rounded-[10px] border border-[#EDE8DF] bg-white px-3.5 py-2.5 text-[14px] text-[#1e2d3d] outline-none transition-colors focus:border-[#375475] focus:shadow-[0_0_0_3px_rgba(55,84,117,0.1)]">
                <option v-for="role in roleOptions" :key="role.name" :value="role.name">{{ role.display_name }}</option>
            </select>
        </div>

        <div class="flex flex-col gap-3 rounded-xl bg-[#F7F4EF] p-4">
            <div class="text-[11px] font-bold uppercase tracking-[0.08em] text-[#375475]">{{ $t('label.password_setup') }}</div>
            <div class="flex flex-col gap-2.5 sm:flex-row">
                <label
                    class="flex flex-1 cursor-pointer items-center gap-2 rounded-[10px] border-2 bg-white px-3.5 py-2.5 transition-all"
                    :class="passwordOption === 'email' ? 'border-[#375475] bg-[#e7edf4]' : 'border-[#EDE8DF]'">
                    <input
                        v-model="passwordOption"
                        type="radio"
                        value="email"
                        class="accent-[#375475]">
                    <div>
                        <div class="text-[13px] font-semibold text-[#1e2d3d]">{{ $t('label.email_invite') }}</div>
                        <div class="mt-px text-[11px] text-[#64748b]">{{ $t('label.user_sets_own') }}</div>
                    </div>
                </label>
                <label
                    class="flex flex-1 cursor-pointer items-center gap-2 rounded-[10px] border-2 bg-white px-3.5 py-2.5 transition-all"
                    :class="passwordOption === 'manual' ? 'border-[#375475] bg-[#e7edf4]' : 'border-[#EDE8DF]'">
                    <input
                        v-model="passwordOption"
                        type="radio"
                        value="manual"
                        class="accent-[#375475]">
                    <div>
                        <div class="text-[13px] font-semibold text-[#1e2d3d]">{{ mode === 'Edit' ? $t('label.reset_password') : $t('label.set_password') }}</div>
                        <div class="mt-px text-[11px] text-[#64748b]">{{ $t('label.admin_defines_now') }}</div>
                    </div>
                </label>
            </div>

            <div v-if="passwordOption === 'manual'" class="flex flex-col gap-2.5">
                <div class="relative">
                    <input
                        v-model="form.password"
                        :type="showPassword ? 'text' : 'password'"
                        :placeholder="$t('label.enter_password')"
                        class="w-full rounded-[10px] border border-[#EDE8DF] bg-white py-2.5 pl-3.5 pr-11 text-[14px] text-[#1e2d3d] outline-none transition-colors focus:border-[#375475] focus:shadow-[0_0_0_3px_rgba(55,84,117,0.1)]">
                    <button type="button" class="absolute right-3 top-1/2 -translate-y-1/2 text-[14px] text-[#94a3b8]" @click="showPassword = !showPassword">
                        <FontAwesomeIcon icon="eye" />
                    </button>
                </div>
                <div class="relative">
                    <input
                        v-model="form.password_confirmation"
                        :type="showPasswordConfirmation ? 'text' : 'password'"
                        :placeholder="$t('label.confirm_password')"
                        class="w-full rounded-[10px] border border-[#EDE8DF] bg-white py-2.5 pl-3.5 pr-11 text-[14px] text-[#1e2d3d] outline-none transition-colors focus:border-[#375475] focus:shadow-[0_0_0_3px_rgba(55,84,117,0.1)]">
                    <button type="button" class="absolute right-3 top-1/2 -translate-y-1/2 text-[14px] text-[#94a3b8]" @click="showPasswordConfirmation = !showPasswordConfirmation">
                        <FontAwesomeIcon icon="eye" />
                    </button>
                </div>
            </div>
        </div>
    </form>

    <template #footer>
        <div class="ml-auto flex gap-2.5">
            <button type="button" class="rounded-[10px] bg-[#EDE8DF] px-5 py-2.5 text-[14px] font-semibold text-[#64748b] hover:bg-[#d5cfc4]" @click="$emit('cancel-modal')">{{ $t('label.cancel') }}</button>
            <button type="button" class="rounded-[10px] bg-[#375475] px-6 py-2.5 text-[14px] font-bold text-white transition-colors hover:bg-[#4a6d93]" @click="save">{{ saveButtonLabel }}</button>
        </div>
    </template>
</FoodFlowModal>
</template>
